import { FunctionComponent, useEffect, useMemo, useRef } from 'react';
import styles from './Filter.module.scss';
import clsx from 'clsx';
import Button from 'components/UI/V3/Button/Button';
import { faLayerGroup } from '@fortawesome/pro-light-svg-icons/faLayerGroup';
import { faAngleDown } from '@fortawesome/pro-regular-svg-icons';
import { PageResult } from '@spike/model';
import { useState } from 'react';
import ClickAwayListener from 'react-click-away-listener';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { Room } from '@spike/room-model';
import { fetchThunk as fetchRoomsThunk, RoomsStatus } from '@spike/rooms-action';
import { useApiClientWrapper } from 'hooks';
import useNonInitialEffect from '@versiondos/hooks';
import ListContent from './ListContent';
import DropdownContainer from './DropdownContainer';
import Header from './Header';
import Footer from './Footer';
import uniq from 'lodash/uniq';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import useOrganizationMarketplaces from 'hooks/useOrganizationMarketplaces';

interface RoomFilterProps {
    selectableRoomIds: Array<number>;
    selectedRoomUnitIds: Array<number>;
    className?: string;
    appearance?: 'button' | 'plain';
    onChangeSelection?: (selectedRoomUnitIds: Array<number>, all: boolean) => void;
}
export const RoomFilter: FunctionComponent<RoomFilterProps> = props => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const { organizationMarketplaces } = useOrganizationMarketplaces();
    const allMarketplacesIds = useMemo(
        () => organizationMarketplaces.map(marketplace => marketplace.id),
        [organizationMarketplaces]
    );

    const rooms = useSelector<RootState, PageResult<Room>>(state => state.rooms.rooms);
    const roomsStatus = useSelector<RootState, RoomsStatus>(state => state.rooms.status);

    const [expanded, setExpanded] = useState(false);
    const expandedRef = useRef(false);
    const ignoreClickAwayRef = useRef(false);

    const [collapsedSelection, setCollapsedSelection] = useState<Array<number>>([]);
    const [collapsedAll, setCollapsedAll] = useState(false);
    const snapshotRef = useRef<{ ids: Array<number>; all: boolean }>({ ids: [], all: false });

    const [selectedRoomIds, setSelectedRoomIds] = useState<Array<number>>(
        props.selectedRoomUnitIds.length === 0
            ? uniq(props.selectableRoomIds)
            : rooms.page
                  .filter(room => room.units.some(unit => uniq(props.selectedRoomUnitIds).includes(unit.id ?? 0)))
                  .map(room => room.id ?? 0)
    );

    const [selectedAll, setSelectedAll] = useState(props.selectedRoomUnitIds.length === 0);
    const selectedRoomIdsRef = useRef(selectedRoomIds);
    const selectedAllRef = useRef(selectedAll);

    const [loading, setLoading] = useState(false);

    const [searchText, setSearchText] = useState('');

    const options = rooms.page
        .filter(room => props.selectableRoomIds.includes(room.id ?? 0))
        .map(room => ({ id: room.id ?? 0, name: room.name }))
        .filter(option => {
            if (searchText === '') {
                return true;
            } else {
                const tokens = searchText.split(' ');
                return option.name.split(' ').some(nameToken => tokens.some(token => nameToken.startsWith(token)));
            }
        });

    const buttonLabel = selectedAll
        ? 'All Rooms'
        : selectedRoomIds.length === 1
        ? rooms.page.find(room => room.id === selectedRoomIds[0])?.name?.substring(0, 16) ?? '1 room'
        : `${selectedRoomIds.length} rooms`;

    useEffect(() => {
        if (rooms.pagination?.pageSize !== 3210) {
            setLoading(true);
            dispatch(fetchRoomsThunk(apiClientWrapper, 1, 3210, allMarketplacesIds));
        }
    }, []);

    const roomIdsForUnits = (unitIds: Array<number>) =>
        uniq(
            rooms.page
                .filter(room => room.units.some(unit => unit.id != null && unitIds.includes(unit.id)))
                .map(room => room.id ?? 0)
        );

    const commitSelection = (roomIds: Array<number>, all: boolean) => {
        selectedRoomIdsRef.current = roomIds;
        selectedAllRef.current = all;
        setSelectedRoomIds(roomIds);
        setSelectedAll(all);
    };

    const syncFromProps = () => {
        if (expandedRef.current) {
            return;
        }

        if (props.selectedRoomUnitIds.length === 0) {
            commitSelection(uniq(props.selectableRoomIds), true);
            return;
        }

        const roomIds = roomIdsForUnits(props.selectedRoomUnitIds);
        if (roomIds.length === 0) {
            return;
        }

        commitSelection(roomIds, false);
    };

    useNonInitialEffect(() => {
        switch (roomsStatus) {
            case RoomsStatus.FetchSuccess:
                syncFromProps();
                setLoading(false);
                break;
            case RoomsStatus.Error:
                setLoading(false);
        }
    }, [roomsStatus]);

    useNonInitialEffect(() => {
        syncFromProps();
    }, [props.selectableRoomIds, props.selectedRoomUnitIds]);

    const rememberSelection = () => {
        const snapshot = { ids: [...selectedRoomIdsRef.current], all: selectedAllRef.current };
        snapshotRef.current = snapshot;
        setCollapsedSelection(snapshot.ids);
        setCollapsedAll(snapshot.all);
    };

    const restoreSelection = () => {
        commitSelection(snapshotRef.current.ids, snapshotRef.current.all);
        setCollapsedSelection(snapshotRef.current.ids);
        setCollapsedAll(snapshotRef.current.all);
    };

    const clickButtonHandler = () => {
        if (!expandedRef.current) {
            rememberSelection();
            expandedRef.current = true;
            setExpanded(true);
            return;
        }

        restoreSelection();
        expandedRef.current = false;
        setExpanded(false);
    };

    const selectionChangeHandler = (selectedIds: Array<number>) => {
        const roomIds = [...selectedIds];
        const all = uniq(props.selectableRoomIds).every(roomId => roomIds.includes(roomId));
        commitSelection(roomIds, all);
    };

    const selectAllHandler = (selected: boolean) => {
        commitSelection(selected ? [...uniq(props.selectableRoomIds)] : [], selected);
    };

    const applyHandler = () => {
        ignoreClickAwayRef.current = true;
        const roomIds = [...selectedRoomIdsRef.current];
        const selectable = uniq(props.selectableRoomIds);
        const allSelected = selectable.length > 0 && selectable.every(roomId => roomIds.includes(roomId));
        const unitIds = allSelected
            ? []
            : uniq(
                  rooms.page
                      .filter(room => roomIds.includes(room.id ?? 0))
                      .flatMap(room => room.units.map(unit => unit.id).filter((id): id is number => id != null))
              );
        const appliedRoomIds = allSelected ? selectable : roomIds;
        snapshotRef.current = { ids: appliedRoomIds, all: allSelected };
        setCollapsedSelection(appliedRoomIds);
        setCollapsedAll(allSelected);
        commitSelection(appliedRoomIds, allSelected);
        expandedRef.current = false;
        setExpanded(false);
        props.onChangeSelection?.(unitIds, allSelected);
    };

    const clickAwayHandler = (event: FocusEvent | MouseEvent | TouchEvent) => {
        event.preventDefault();
        event.stopPropagation();
        if (ignoreClickAwayRef.current) {
            ignoreClickAwayRef.current = false;
            return;
        }
        restoreSelection();
        expandedRef.current = false;
        setExpanded(false);
    };

    const header =
        uniq(props.selectableRoomIds).length > 1 ? (
            <Header
                allLabel="All Rooms"
                selectedAll={selectedAll}
                onClose={() => {
                    restoreSelection();
                    expandedRef.current = false;
                    setExpanded(false);
                }}
                onSearch={setSearchText}
                onSelectAll={selectAllHandler}
            />
        ) : null;

    const footer = (
        <Footer
            onApply={applyHandler}
            disabled={uniq(selectedRoomIds).length === 0}
        />
    );

    const list = (
        <ClickAwayListener onClickAway={clickAwayHandler}>
            <div>
                <DropdownContainer
                    header={header}
                    footer={footer}
                >
                    <ListContent
                        options={options}
                        selectedIds={selectedRoomIds}
                        onChange={selectionChangeHandler}
                    />
                </DropdownContainer>
            </div>
        </ClickAwayListener>
    );

    return (
        <div className={clsx(styles.container, props.className)}>
            {props.appearance === 'plain' ? (
                <button
                    type="button"
                    className={styles.plainTrigger}
                    onClick={clickButtonHandler}
                >
                    <span>{buttonLabel}</span>
                    <FontAwesomeIcon icon={faAngleDown} />
                </button>
            ) : (
                <>
                    <Button
                        variant={'outlined'}
                        label={buttonLabel}
                        leftIcon={faLayerGroup}
                        size="large"
                        onClick={clickButtonHandler}
                        className={styles.button}
                    />
                    <div
                        className={styles.mobileButton}
                        onClick={clickButtonHandler}
                    >
                        <FontAwesomeIcon icon={faLayerGroup} />
                    </div>
                </>
            )}
            {expanded && list}
        </div>
    );
};

export default RoomFilter;
